import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, PackageIcon, TruckIcon, HomeIcon, MapPinIcon, CreditCardIcon, TagIcon, ClockIcon, StoreIcon, PhoneIcon, ExternalLinkIcon, NavigationIcon, ChevronRightIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { AnnouncementBar } from '../components/layout/AnnouncementBar';
import { MobileDeliverToPill } from '../components/layout/MobileDeliverToPill';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { MobileHomeHeader } from '../components/MobileHomeHeader';
import { OrderRatingModal } from '../components/modals/OrderRatingModal';
import { PRODUCT_IMAGES } from '../data/productImages';
export function ThankYouPage() {
  const navigate = useNavigate();
  const [confettiActive, setConfettiActive] = useState(true);
  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [showDeliveryTracking, setShowDeliveryTracking] = useState(false);
  const [showRating, setShowRating] = useState(false);

  // Scroll to top on mount and show rating after delay
  useEffect(() => {
    window.scrollTo(0, 0);
    const timer = setTimeout(() => {
      setShowRating(true);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);

  // Mock order data
  const orderData = {
    orderNumber: 'DN38291',
    orderDate: 'December 20, 2024',
    orderTime: '2:30 PM',
    deliveryType: 'pickup',
    estimatedDelivery: 'Same Day',
    deliveryDate: 'December 20, 2024',
    deliveryTime: '6:00 PM - 8:00 PM',
    shippingAddress: {
      name: 'Ahmed Mohammed',
      phone: '+966 50 123 4567',
      address: 'Al Malqa District, Building 123, Apt 4B',
      city: 'Riyadh',
      postalCode: '12345'
    },
    pickupBranch: {
      name: 'Innova Pharmacy - Riyadh Park',
      address: 'Riyadh Park Mall, Al Malaz District, Riyadh',
      city: 'Riyadh',
      district: 'Al Malaz',
      phone: '+966 11 234 5678',
      coordinates: {
        lat: 24.7136,
        lng: 46.6753
      },
      hours: {
        open: '08:00 AM',
        close: '11:00 PM'
      },
      pickupTime: 'Today, 4:00 PM - 8:00 PM'
    },
    paymentMethod: {
      type: 'Credit Card',
      last4: '4242',
      brand: 'Visa'
    },
    items: [{
      id: 1,
      name: 'Optimum Nutrition Gold Standard 100% Whey Protein',
      quantity: 2,
      price: 29.99,
      image: 'https://img.innovapharmacy.com/media/catalog/product/4/5/4537b3e3-4206-41fc-b34e-53ddce591f66_1.jpg'
    }, {
      id: 2,
      name: 'C4 Original Pre Workout Powder',
      quantity: 1,
      price: 19.99,
      image: 'https://img.innovapharmacy.com/media/catalog/product/2/1/213099.jpg'
    }],
    pricing: {
      subtotal: 79.97,
      shipping: 0,
      discount: 10.0,
      tax: 12.0,
      total: 81.97
    },
    coupon: {
      code: 'WELCOME10',
      discount: 10.0
    }
  };
  const getGoogleMapsLink = () => {
    if (orderData.deliveryType === 'pickup' && orderData.pickupBranch) {
      const {
        lat,
        lng
      } = orderData.pickupBranch.coordinates;
      return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    }
    return null;
  };
  return <HelmetProvider data-id="element-8541" data-mp="axvoleky8nk5">
      <SEO title="Order Confirmed - Thank You" description="Your order has been successfully placed. Thank you for shopping with Innova Pharmacy." canonical="/en/thank-you/" hreflangAr="/thank-you/" hreflangEn="/en/thank-you/" hreflangDefault="/thank-you/" data-id="element-8542" data-mp="fw715x2mpc62" />
      <div className="flex flex-col min-h-screen bg-gray-50/50" data-id="element-8543" data-mp="3uatx26rdzyrx">
        {/* Desktop Header */}
        <div className="hidden md:block" data-id="element-8544" data-mp="3q9qehchyfon0">
          <AnnouncementBar data-id="element-8545" data-mp="3q8p47a8rj66c" />
          <MobileDeliverToPill selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} data-id="element-8546" data-mp="3q7e019benwep" />
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} data-id="element-8547" data-mp="3ga996w6m7crk" />
        </div>

        <main className="flex-grow pt-[var(--header-height-mobile)] md:pt-[var(--header-height-desktop)]" data-id="element-8548" data-mp="37cywr7i29hl2">
          {/* Mobile Header Overlay - Only on mobile */}
          <div className="md:hidden fixed top-0 left-0 right-0 z-50" data-id="element-8549" data-mp="15jr99asanto3">
            <MobileHomeHeader selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} alwaysWhiteBackground={true} data-id="element-8550" data-mp="2nhnxdhipzezi" />
          </div>

          {/* Page Hero Breadcrumb */}
          <PageHeroBreadcrumb breadcrumbs={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'Order Confirmed'
        }]} title="Thank You for Your Order" description="Your order has been successfully placed" data-id="element-8551" data-mp="2x91uxjr424q0" />

          {/* Main Content */}
          <div className="py-8 px-4" data-id="element-8552" data-mp="2dl1f90hs3216">
            <div className="max-w-5xl mx-auto" data-id="element-8553" data-mp="2ii1kxaapu2rr">
              {/* Success Header */}
              <div className="bg-white rounded-2xl p-8 mb-6 text-center shadow-sm border border-gray-100" data-id="element-8554" data-mp="3oydaimzh2cbq">
                <div className="relative mb-8 flex justify-center" data-id="element-8555" data-mp="3twf1rv71xhya">
                  <motion.div initial={{
                  y: -20,
                  opacity: 0
                }} animate={{
                  y: 0,
                  opacity: 1
                }} transition={{
                  type: 'spring',
                  bounce: 0.5,
                  duration: 0.8
                }} className="w-24 h-24 bg-[#FFD6E5] rounded-2xl flex items-center justify-center border-2 border-gray-900 shadow-[4px_4px_0px_0px_rgba(17,24,39,1)]" data-id="element-8556" data-mp="3tyrhs2t91i8p">
                    <PackageIcon className="w-12 h-12 text-white fill-white" strokeWidth={1} data-id="element-8557" data-mp="1b47bx5a2ui15" />
                  </motion.div>
                  <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  delay: 0.5,
                  type: 'spring',
                  bounce: 0.5
                }} className="absolute -bottom-3 ml-16 bg-[#A3E635] rounded-full p-1 border-2 border-gray-900" data-id="element-8558" data-mp="165mj3qx5n0mq">
                    <CheckCircleIcon className="w-6 h-6 text-gray-900" strokeWidth={2.5} data-id="element-8559" data-mp="scet23oq2d75" />
                  </motion.div>
                </div>
                
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3" data-id="element-8560" data-mp="2z28cx97wnjyg">
                  Thanks for your order!
                </h1>
                <p className="text-gray-600 mb-6 max-w-md mx-auto" data-id="element-8561" data-mp="33u9mgndouwks">
                  Did you know? Spending more than SAR 250 will get you 5% cashback on your next purchase.
                </p>
                <div className="inline-flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-5 py-2.5" data-id="element-8562" data-mp="2u3osjd8ymg87">
                  <span className="text-sm text-gray-600" data-id="element-8563" data-mp="3hqddk7t9mot1">Order Number:</span>
                  <span className="text-lg font-bold text-[#1E2B55]" data-id="element-8564" data-mp="3mr128mu0utk3">
                    #{orderData.orderNumber}
                  </span>
                </div>
              </div>

              {/* Main Content Grid */}
              <div className="grid lg:grid-cols-3 gap-6" data-id="element-8565" data-mp="3dsz7gp0vsx59">
                {/* Left Column - Order Details */}
                <div className="lg:col-span-2 space-y-6" data-id="element-8566" data-mp="1xez7xf5y79ij">
                  {/* Delivery Type Badge */}
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-8567" data-mp="2t1sypx87m669">
                    <div className="flex items-center justify-center gap-4" data-id="element-8568" data-mp="2szg9cz04gtj6">
                      {orderData.deliveryType === 'delivery' ? <>
                          <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center" data-id="element-8569" data-mp="2k1cxyviupd53">
                            <TruckIcon className="w-7 h-7 text-blue-600" data-id="element-8570" data-mp="2f44emrlyv329" />
                          </div>
                          <div className="text-left" data-id="element-8571" data-mp="3crl6rwrhyl70">
                            <h3 className="text-xl font-bold text-gray-900" data-id="element-8572" data-mp="37rqgnvfjpzn0">
                              Home Delivery
                            </h3>
                            <p className="text-sm text-gray-500 mt-1" data-id="element-8573" data-mp="2y0vt8nqkctzz">
                              Your order will be delivered to your address
                            </p>
                          </div>
                        </> : <>
                          <div className="w-14 h-14 bg-green-50 rounded-2xl flex items-center justify-center" data-id="element-8574" data-mp="3qnq9f8gcl77n">
                            <StoreIcon className="w-7 h-7 text-green-600" data-id="element-8575" data-mp="6ueqeyhb8hlm" />
                          </div>
                          <div className="text-left" data-id="element-8576" data-mp="btr874tbqyhc">
                            <h3 className="text-xl font-bold text-gray-900" data-id="element-8577" data-mp="bs6etiiqxll2">
                              Store Pickup
                            </h3>
                            <p className="text-sm text-gray-500 mt-1" data-id="element-8578" data-mp="kupig4ucw28d">
                              Pick up your order from the selected branch
                            </p>
                          </div>
                        </>}
                    </div>
                  </div>

                  {/* Order Timeline */}
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-8579" data-mp="ziai1os0vjbj">
                    <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2" data-id="element-8580" data-mp="14jq1jcejxhrt">
                      <ClockIcon className="w-5 h-5 text-[#1E2B55]" data-id="element-8581" data-mp="18ka3cta2l9ka" />
                      Order Timeline
                    </h2>
                    <div className="space-y-6" data-id="element-8582" data-mp="48syvxqfiy8">
                      <div className="flex items-start gap-4" data-id="element-8583" data-mp="50qicaw3qr5">
                        <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-8584" data-mp="53lqqbq56afl">
                          <CheckCircleIcon className="w-5 h-5 text-green-600" data-id="element-8585" data-mp="9v3sv33wih25" />
                        </div>
                        <div className="flex-1 pt-2" data-id="element-8586" data-mp="esdee7o668kp">
                          <h3 className="font-bold text-gray-900" data-id="element-8587" data-mp="iuqikrn9pyfd">
                            Order Confirmed
                          </h3>
                          <p className="text-sm text-gray-500 mt-1" data-id="element-8588" data-mp="nuuz2ho6et5l">
                            {orderData.orderDate} at {orderData.orderTime}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4" data-id="element-8589" data-mp="2pnauvengv3hk">
                        <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-8590" data-mp="2ulcb7rticopc">
                          <PackageIcon className="w-5 h-5 text-blue-600" data-id="element-8591" data-mp="2n2bk65zw8niv" />
                        </div>
                        <div className="flex-1 pt-2" data-id="element-8592" data-mp="2i8qqur98whq0">
                          <h3 className="font-bold text-gray-900" data-id="element-8593" data-mp="2dafeo9vh9v4m">
                            Processing Order
                          </h3>
                          <p className="text-sm text-gray-500 mt-1" data-id="element-8594" data-mp="28dp3o0yj5pq2">
                            We're preparing your items
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4" data-id="element-8595" data-mp="30ygjp8tg6tfa">
                        <div className="w-10 h-10 bg-gray-50 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-8596" data-mp="2w0ezgkkaco28">
                          {orderData.deliveryType === 'delivery' ? <TruckIcon className="w-5 h-5 text-gray-400" data-id="element-8597" data-mp="1kncl7w5nsal8" /> : <StoreIcon className="w-5 h-5 text-gray-400" data-id="element-8598" data-mp="1fmf5dq84fsmw" />}
                        </div>
                        <div className="flex-1 pt-2" data-id="element-8599" data-mp="7ln65r2xegvz">
                          <h3 className="font-medium text-gray-600" data-id="element-8600" data-mp="bpmhlv9cmpih">
                            {orderData.deliveryType === 'delivery' ? 'Estimated Delivery' : 'Ready for Pickup'}
                          </h3>
                          <p className="text-sm text-gray-400 mt-1" data-id="element-8601" data-mp="3k5eo4na21f99">
                            {orderData.deliveryType === 'delivery' ? orderData.deliveryDate : orderData.pickupBranch?.pickupTime}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Order Items */}
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-8602" data-mp="317bu1u2ngs4l">
                    <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2" data-id="element-8603" data-mp="364by5zfxvkt2">
                      <PackageIcon className="w-5 h-5 text-[#1E2B55]" data-id="element-8604" data-mp="2mkmu5r4hkcxo" />
                      Order Items
                    </h2>
                    <div className="space-y-4" data-id="element-8605" data-mp="2ijt7f12s546d">
                      {orderData.items.map(item => <div key={item.id} className="flex gap-4 pb-4 border-b border-gray-100 last:border-0 last:pb-0" data-id="element-8606" data-mp="2wj2k3qrs1g5l">
                          <div className="w-20 h-20 bg-gray-50 rounded-xl p-2 flex-shrink-0" data-id="element-8607" data-mp="35gmmx45afkib">
                            <img src={item.image} alt={item.name} className="w-full h-full object-contain mix-blend-multiply" data-id="element-8608" data-mp="31gm12yfv0718" />
                          </div>
                          <div className="flex-1" data-id="element-8609" data-mp="3p787yme4nl9n">
                            <h3 className="font-medium text-gray-900 mb-1 line-clamp-2" data-id="element-8610" data-mp="3k7c5wsozq3wq">
                              {item.name}
                            </h3>
                            <p className="text-sm text-gray-500" data-id="element-8611" data-mp="1hkeu4jxq1av4">
                              Qty: {item.quantity}
                            </p>
                          </div>
                          <div className="text-right" data-id="element-8612" data-mp="1rbt1fdvwzeyt">
                            <p className="font-bold text-gray-900" data-id="element-8613" data-mp="1w9l0cruz81ci">
                              SAR {(item.price * item.quantity).toFixed(2)}
                            </p>
                          </div>
                        </div>)}
                    </div>
                  </div>

                  {/* Shipping Address or Pickup Branch Details */}
                  {orderData.deliveryType === 'delivery' ? <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-8614" data-mp="1djuy5jovwdak">
                      <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-8615" data-mp="2jy42sqrchjwo">
                        <MapPinIcon className="w-5 h-5 text-[#1E2B55]" data-id="element-8616" data-mp="2owxeqeqzh2uq" />
                        Shipping Address
                      </h2>
                      <div className="space-y-1" data-id="element-8617" data-mp="dcpusti710sf">
                        <p className="font-bold text-gray-900" data-id="element-8618" data-mp="8cv34nqlg76b">
                          {orderData.shippingAddress.name}
                        </p>
                        <p className="text-gray-600" data-id="element-8619" data-mp="3iqxc7t2e56t">
                          {orderData.shippingAddress.phone}
                        </p>
                        <p className="text-gray-600 mt-2" data-id="element-8620" data-mp="3psjxxg9s1trm">
                          {orderData.shippingAddress.address}
                        </p>
                        <p className="text-gray-600" data-id="element-8621" data-mp="3lsszqrhx8bd2">
                          {orderData.shippingAddress.city},{' '}
                          {orderData.shippingAddress.postalCode}
                        </p>
                      </div>
                    </div> : <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-8622" data-mp="1ezh53vd4z52j">
                      <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-8623" data-mp="1a1ytfx7qibqu">
                        <StoreIcon className="w-5 h-5 text-[#1E2B55]" data-id="element-8624" data-mp="34g2zo34krdok" />
                        Pickup Branch Details
                      </h2>
                      <div className="space-y-4" data-id="element-8625" data-mp="2ujq6d6cxz1uc">
                        {/* Branch Name */}
                        <div data-id="element-8626" data-mp="2zi1dvafrl09e">
                          <p className="text-sm text-gray-500 mb-1" data-id="element-8627" data-mp="2zj2stpzwi6we">
                            Branch Name
                          </p>
                          <p className="font-bold text-gray-900 text-lg" data-id="element-8628" data-mp="3n6166esq4yq3">
                            {orderData.pickupBranch?.name}
                          </p>
                        </div>
                        {/* Location */}
                        <div data-id="element-8629" data-mp="3i89dvjo63ald">
                          <p className="text-sm text-gray-500 mb-1" data-id="element-8630" data-mp="3i7hgc3b0f2sg">Location</p>
                          <div className="flex items-start gap-2" data-id="element-8631" data-mp="21u9fid4yzt08">
                            <MapPinIcon className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" data-id="element-8632" data-mp="26tm4p4jbdc5z" />
                            <div data-id="element-8633" data-mp="ow0joh2uuzur">
                              <p className="text-gray-900 font-medium" data-id="element-8634" data-mp="ouxvigwx3bij">
                                {orderData.pickupBranch?.address}
                              </p>
                              <p className="text-sm text-gray-600 mt-1" data-id="element-8635" data-mp="xwf9ntqrl34l">
                                {orderData.pickupBranch?.district},{' '}
                                {orderData.pickupBranch?.city}
                              </p>
                            </div>
                          </div>
                        </div>
                        {/* Phone */}
                        <div data-id="element-8636" data-mp="14bq6xeas5c8">
                          <p className="text-sm text-gray-500 mb-1" data-id="element-8637" data-mp="eycpptd2pwdn">
                            Branch Phone
                          </p>
                          <div className="flex items-center gap-2" data-id="element-8638" data-mp="a5jld6lioq23">
                            <PhoneIcon className="w-5 h-5 text-gray-600" data-id="element-8639" data-mp="3im4mk17wqeun" />
                            <a href={`tel:${orderData.pickupBranch?.phone}`} className="text-[#1E2B55] hover:underline font-medium" data-id="element-8640" data-mp="3elaxv5mn4w0p">
                              {orderData.pickupBranch?.phone}
                            </a>
                          </div>
                        </div>
                        {/* Working Hours */}
                        <div data-id="element-8641" data-mp="laqw9xyc8nui">
                          <p className="text-sm text-gray-500 mb-1" data-id="element-8642" data-mp="lc20fyvp3xm5">
                            Working Hours
                          </p>
                          <div className="flex items-center gap-2" data-id="element-8643" data-mp="v8ohsdotc6jf">
                            <ClockIcon className="w-5 h-5 text-gray-600" data-id="element-8644" data-mp="v6tyf3t6wmhg" />
                            <p className="text-gray-900 font-medium" data-id="element-8645" data-mp="100odftxuoda7">
                              {orderData.pickupBranch?.hours.open} -{' '}
                              {orderData.pickupBranch?.hours.close}
                            </p>
                          </div>
                        </div>
                        {/* Pickup Time */}
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4" data-id="element-8646" data-mp="1dwtl5k1k5php">
                          <p className="text-sm text-gray-600 mb-1" data-id="element-8647" data-mp="1o0qe0iaj5s0m">
                            Estimated Pickup Time
                          </p>
                          <p className="text-lg font-bold text-[#1E2B55]" data-id="element-8648" data-mp="1e5ygtfkv176d">
                            {orderData.pickupBranch?.pickupTime}
                          </p>
                        </div>
                        {/* Google Maps Link */}
                        {getGoogleMapsLink() && <a href={getGoogleMapsLink() || '#'} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full bg-[#1E2B55] hover:bg-[#162041] text-white font-medium py-3 rounded-xl transition-colors" data-id="element-8649" data-mp="21vizryh0k4fn">
                            <NavigationIcon className="w-5 h-5" data-id="element-8650" data-mp="2kt3nh281kwyo" />
                            Get Directions on Google Maps
                            <ExternalLinkIcon className="w-4 h-4" data-id="element-8651" data-mp="fad5eyasdb0d" />
                          </a>}
                      </div>
                    </div>}

                  {/* Payment Method */}
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-8652" data-mp="69ye9ib551oy">
                    <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-8653" data-mp="1bdhjsv3l059">
                      <CreditCardIcon className="w-5 h-5 text-[#1E2B55]" data-id="element-8654" data-mp="yylu2yd4eyv4" />
                      Payment Method
                    </h2>
                    <div className="flex items-center gap-4" data-id="element-8655" data-mp="p67ry4i4rq8f">
                      <div className="w-14 h-10 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center" data-id="element-8656" data-mp="p4myki7jydc5">
                        <CreditCardIcon className="w-6 h-6 text-gray-600" data-id="element-8657" data-mp="1hnsjf48f6opw" />
                      </div>
                      <div data-id="element-8658" data-mp="1cw26qrm0fovb">
                        <p className="font-bold text-gray-900" data-id="element-8659" data-mp="1cvseexdht5mg">
                          {orderData.paymentMethod.brand}{' '}
                          {orderData.paymentMethod.type}
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-8660" data-mp="1p71ssxgxeney">
                          •••• •••• •••• {orderData.paymentMethod.last4}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Order Summary */}
                <div className="lg:col-span-1" data-id="element-8661" data-mp="234hyu7vb8f66">
                  <div className="bg-white rounded-2xl p-6 sticky top-6 shadow-sm border border-gray-100" data-id="element-8662" data-mp="284vwcvzac6py">
                    <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-8663" data-mp="2cxfc9zlg3dxx">
                      Order Summary
                    </h2>
                    <div className="space-y-4 mb-6" data-id="element-8664" data-mp="2hxjsdoify6nm">
                      <div className="flex justify-between text-gray-600" data-id="element-8665" data-mp="2mvczqkvhd6ph">
                        <span data-id="element-8666" data-mp="2qvvryg1aces7">Subtotal</span>
                        <span className="font-medium text-gray-900" data-id="element-8667" data-mp="2qwyfcdzefz3d">SAR {orderData.pricing.subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-gray-600" data-id="element-8668" data-mp="msaqehbx1gyf">
                        <span data-id="element-8669" data-mp="mq7p315bc1ee">Shipping</span>
                        <span className="text-green-600 font-medium" data-id="element-8670" data-mp="10k8ntuw9loer">
                          {orderData.pricing.shipping === 0 ? 'FREE' : `SAR ${orderData.pricing.shipping.toFixed(2)}`}
                        </span>
                      </div>
                      {orderData.coupon && <div className="flex justify-between text-green-600" data-id="element-8671" data-mp="15ijxa4indwwg">
                          <span className="flex items-center gap-1" data-id="element-8672" data-mp="1jh9v5mdak0lv">
                            <TagIcon className="w-4 h-4" data-id="element-8673" data-mp="1fgq1isrxff4h" />
                            Coupon ({orderData.coupon.code})
                          </span>
                          <span className="font-medium" data-id="element-8674" data-mp="3lc0sory4f3c6">
                            -SAR {orderData.coupon.discount.toFixed(2)}
                          </span>
                        </div>}
                      <div className="flex justify-between text-gray-600" data-id="element-8675" data-mp="376s6pe72fxgb">
                        <span data-id="element-8676" data-mp="3b57x7s2cvnyh">Tax (VAT 15%)</span>
                        <span className="font-medium text-gray-900" data-id="element-8677" data-mp="2xf4j5odmr0k4">SAR {orderData.pricing.tax.toFixed(2)}</span>
                      </div>
                      <div className="border-t border-gray-100 pt-4" data-id="element-8678" data-mp="32c4p7zahc3b8">
                        <div className="flex justify-between items-center" data-id="element-8679" data-mp="2ng5fylbmvx4i">
                          <span className="text-lg font-bold text-gray-900" data-id="element-8680" data-mp="2sf9u7qdf0l1p">
                            Total
                          </span>
                          <span className="text-2xl font-bold text-[#1E2B55]" data-id="element-8681" data-mp="2ehbdwffjaejk">
                            SAR {orderData.pricing.total.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="space-y-3" data-id="element-8682" data-mp="hmykigt6q1m0">
                      <button onClick={() => navigate('/')} className="w-full bg-black hover:bg-gray-800 text-white font-bold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2" data-id="element-8683" data-mp="2dqd33nd2sv3b">
                        Continue Shopping
                      </button>
                      <button onClick={() => navigate('/account/orders')} className="w-full bg-gray-50 text-gray-900 hover:bg-gray-100 font-bold py-3.5 rounded-xl transition-colors" data-id="element-8684" data-mp="1zqaj9vl7jrlf">
                        Track Your Order
                      </button>
                    </div>

                    {/* Need Help */}
                    <div className="mt-6 pt-6 border-t border-gray-100 text-center" data-id="element-8685" data-mp="1g45ywekop4rh">
                      <p className="text-sm text-gray-500 mb-2" data-id="element-8686" data-mp="1c07x1w3z5fuq">
                        Need help with your order?
                      </p>
                      <button className="text-[#1E2B55] hover:text-[#162041] font-bold text-sm" data-id="element-8687" data-mp="1749ame27i7wy">
                        Contact Customer Support
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        <div className="hidden md:block" data-id="element-8688" data-mp="jwvawxeovwi">
          <Footer data-id="element-8689" data-mp="iu74wrgx7ka" />
        </div>
        <MobileFooterNav data-id="element-8690" data-mp="j3usdp1j2mt" />
        <PharmacistChat data-id="element-8691" data-mp="2zz0mmqwzff8t" />
        <div className="h-16 md:hidden" data-id="element-8692" data-mp="2zztjn0652qdr"></div>

        <OrderRatingModal isOpen={showRating} orderNumber={orderData.orderNumber} onClose={() => setShowRating(false)} onSubmit={rating => {
        sessionStorage.setItem(`order-rating-${orderData.orderNumber}`, String(rating));
      }} data-id="element-8693" data-mp="2zwxkj8ik1vnq" />
      </div>
    </HelmetProvider>;
}